<template>
  <v-dialog
    v-model="periodDialog"
    :data-qa="'modal-date-period-' + (periodDialog ? 'open' : 'close')"
    persistent
    width="755">
    <template #activator="{ on }">
      <slot
        name="activator"
        :on="on"
        :open="open">
        <v-chip
          id="periodDialogBtn"
          data-qa="btn-modal-date-period-open"
          :end="search[1]"
          :start="search[0]"
          v-on="on"
          @click="open">
          {{ text }}
          <sIcon
            class="ml-2 my-auto"
            size="14"
            type="calendar"/>
        </v-chip>
      </slot>
    </template>
    <v-card
      id="periodDialog"
      class="dialog pb-6 main__light_green"
      elevation="0">
      <div
        class="heaader">
        <div
          class="title">
          {{ $t('Purchasing.Period') }}
        </div>
        <div
          class="exit-btn">
          <sButton
            data-qa="btn-close-dialog"
            icon="cross"
            type="text"
            @click="periodDialog = false"/>
        </div>
      </div>
      <v-card-actions class="px-10 pb-0 d-flex flex-column align-start">
        <div
          v-if="showPeriods"
          class="mt-4">
          <div class="font-weight-medium text-start">
            {{ $t('Purchasing.SelectRequiredTimeInterval') }}
          </div>
          <v-row
            class="pt-2"
            no-gutters>
            <v-chip-group
              v-model="selectedRange"
              active-class="main__green selected-chip">
              <v-chip
                v-for="(periodItem, periodIndex) in periods"
                :key="periodIndex"
                class="btn-period-type mx-2"
                :class="{ 'ml-0': periodIndex === 0 }"
                :data-qa="'type-date-period-' + periodItem.type"
                :outlined="selectedRange !== periodIndex"
                @click="setDateRangeByPeriod(periodIndex, periodItem.type)">
                <div class="flex">
                  <div style="line-height: 18px;">
                    {{ periodItem.name }}
                  </div>
                  <sIcon
                    v-if="periodIndex !== 3 && selectedRange === periodIndex"
                    class="ml-1"
                    line-height="20px"
                    size="14"
                    type="cross"/>
                </div>
              </v-chip>
            </v-chip-group>
          </v-row>
        </div>
        <div class="choose-dates-inputs pt-4 pb-1">
          <div class="text-left font-weight-medium">
            {{ $t('Purchasing.ChooseDates') }}
          </div>
          <v-row
            class="mt-3"
            no-gutters>
            <DateField
              clearable
              data-qa="date-field-date-from"
              :disabled="mutableOptions.from.disabled"
              :error="!!error"
              :model="dateFrom"
              :prefix="$t('Common.DateFrom')"
              :readonly="false"
              style="width: 180px; max-height: 40px;"
              :text="dateFrom | formatDate"
              @changeText="(value) => { setDateRange([value, dateTo]); }"/>
            <DateField
              class="ml-4"
              clearable
              data-qa="date-field-date-to"
              :disabled="mutableOptions.to.disabled"
              :error="!!error"
              :model="dateTo"
              :prefix="$t('Common.DateTo')"
              :readonly="false"
              style="width: 180px; max-height: 40px;"
              :text="dateTo | formatDate"
              @changeText="(value) => setDateRange([dateFrom, value])"/>
          </v-row>
          <v-expand-transition>
            <div
              v-show="error"
              class="mt-2 h8-text pl-3 secondary__red--text text-left">
              {{ error }}
            </div>
          </v-expand-transition>
        </div>
      </v-card-actions>
      <v-card-text class="pt-5 calendars">
        <DatePickerPair
          ref="date-picker-pair"
          :fixed_value="fixedValue"
          :options="mutableOptions"
          :period.sync="period"
          range
          @chooseDate="checkRange"/>
      </v-card-text>
      <v-card-actions class="footer pl-10 pt-0 mt-7 align-start">
        <v-btn
          class="black white--text px-8"
          rounded
          @click="apply()">
          {{ $t(`Common.Apply`) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import DateField from 'components/common/inputs/DateField.vue'
import DatePickerPair from 'components/common/blocks/DatePickerPair.vue'

import { datePickerMixin, componentOptions} from 'mixins'

/**
 * @category dialog
 * Диалог выбора периода дат
 * @vue-data {Boolean} periodDialog - Отображение диалога выбора периода
 * @vue-data {Array} period - Диапазон дат. Используется внутри компонента yyyy-mm-dd
 * @vue-data {String} dateFrom - Дата начала диапазона dd.mm.yyyy
 * @vue-data {String} dateTo - Дата завершения диапазона dd.mm.yyyy
 * @vue-data {Number} selectedRange - Текущий тип периода дат
 * @vue-data {String} text - Текст кнопки выбора периода
 *
 * @vue-prop {Array} search - Входящий диапазон дат
 * @vue-prop {Boolean} [showPeriods=true] - отображение выбора периодов
 * @vue-prop {Object} options - Опции компонента (пробрасываются в mutableOptions)
 *
 * @mixes datePickerMixin - справочник periods
 */
export default {
  name: 'dateRangeDialog',
  mixins: [datePickerMixin, componentOptions],
  data() {
    return {
      format: 'DD.MM.YYYY',
      error: null,
      periodDialog: false,
      period: [],
      dateFrom: '',
      dateTo: '',
      selectedRange: null,
      text: '',
      mutableOptions: {
        from: {
          disabled: false,
          default: null,
          min: null
        },
        to: {
          disabled: false,
          default: null,
          min: null
        },
      },
    }
  },
  methods: {
    /**
     * Изменение периода вручную
     */
    setDateRange(period) {
      const dateFrom = period[0] ? this.$moment(period[0], this.format).format('YYYY-MM-DD') : null
      const dateTo = period[1] ? this.$moment(period[1], this.format).format('YYYY-MM-DD') : null
      if (dateTo && dateFrom && this.$moment(dateTo).isBefore(dateFrom)) {
        this.error = this.$t('Tenders.StartDateCannotExceedEndDate')
        return
      }
      this.error = ''
      this.period = [dateFrom, dateTo]

      // this.$refs.dateRange.showSelectedPeriod([dateFrom, dateTo])
      this.$nextTick(() => {
        this.checkRange(this.period)
      })
    },
    /**
     * Изменение периода на основании типа периода дат
     */
    setDateRangeByPeriod(index, periodType) {
      let dateFrom = null
      let dateTo = null
      if (index === this.selectedRange) {
        this.selectedRange = null
      } else {
        const startPeriod = this.$moment().startOf(periodType)
        const endPeriod = this.$moment().endOf(periodType)
        dateFrom = startPeriod.format(this.format)
        dateTo = endPeriod.format(this.format)
        // this.$refs.dateRange.showSelectedPeriod([startPeriod.format('YYYY-MM'), endPeriod.format('YYYY-MM')])
      }
      this.setDateRange([dateFrom, dateTo])
    },
    /**
     * Заполняем данные при открытии диалога выбора периода
     */
    open() {
      this.period = this.search.slice(0)
      if (!this.period[0] && this.mutableOptions.from.default) {
        this.period[0] = this.mutableOptions.from.default
        this.dateFrom = this.$moment(this.mutableOptions.from.default, 'YYYY-MM-DD').format(this.format)
      }
      if (!this.period[1] && this.mutableOptions.to.default) {
        this.period[1] = this.mutableOptions.to.default
        this.dateTo = this.$moment(this.mutableOptions.to.default, 'YYYY-MM-DD').format(this.format)
      }

      this.$nextTick(() => {
        this.checkRange(this.period)
      })
    },
    /*
     * Подтверждаем выбор периода
     */
    apply() {
      // this.period[1] = this.period[1] || this.period[0]
      // this.period[0] = this.period[0] || this.period[1]
      this.$nextTick(() => {
        this.$emit('update:search', this.period)
        this.checkRange(this.period)
        this.$emit('toggleClear')
        this.periodDialog = false
        this.$emit('apply', this.period)
      })
    },
    /**
     * Проверяем период на соответсвие типа периода
     * Проставляем текст для кнопки выбора периода
     */
    checkRange() {
      this.error = ''
      this.text = this.$t('Purchasing.AllTheTime')
      this.selectedRange = 3
      if (!this.dateTo && !this.dateFrom) {
        return
      }

      if (!this.dateTo || !this.dateFrom || this.dateTo === this.dateFrom) {
        this.text = this.dateTo || this.dateFrom
        return
      } else {
        this.text = `${this.dateFrom} - ${this.dateTo}`
      }
      this.periods.forEach((period, periodNumber) => {
        let dateToCheck = period.type ? this.$moment(this.dateFrom, this.format).endOf(period.type) : null
        let dateFromCheck = period.type ? this.$moment(this.dateTo, this.format).startOf(period.type) : null

        const currentYear = new Date().getFullYear()
        const currentMonth = new Date().getMonth()
        if (period.type === 'week') {
          let day = new Date().getDay()
          const diff = new Date().getDate() - day + (day == 0 ? -6 : 1)
          let firstDay = new Date(new Date().setDate(diff))
          dateFromCheck = new Date(dateFromCheck).setFullYear(currentYear, currentMonth, firstDay.getDate())
          dateToCheck = this.$moment(new Date(dateFromCheck)).endOf(period.type)
        } else if (period.type === 'month') {
          dateFromCheck = new Date(dateFromCheck).setFullYear(currentYear, currentMonth, 1)
          dateToCheck = this.$moment(new Date(dateFromCheck)).endOf(period.type)
        } else if (period.type === 'year') {
          dateFromCheck = new Date(dateFromCheck).setFullYear(currentYear, 0, 1)
          dateToCheck = this.$moment(new Date(dateFromCheck)).endOf(period.type)
        } else {
          dateFromCheck = period.type ? this.$moment(this.dateTo, this.format).startOf(period.type).format(this.format) : null
          dateToCheck = period.type ? this.$moment(this.dateFrom, this.format).endOf(period.type).format(this.format) : null
        }


        dateToCheck = dateToCheck ? this.$moment(dateToCheck).format(this.format) : null
        dateFromCheck = dateFromCheck ? this.$moment(dateFromCheck).format(this.format) : null

        if (this.dateTo === dateToCheck && this.dateFrom === dateFromCheck) {
          this.selectedRange = periodNumber
          this.text = period.name
        }
      })
    },
  },
  filters: {
    formatDate(value) {
      return value && Date.parse(value) ? value : ''
    },
  },
  created() {
    this.format = this.$i18n.locale === 'en' ? 'MM-DD-YYYY' : 'DD.MM.YYYY'
    this.period = this.search.slice(0)
    this.$nextTick(() => {
      this.checkRange()
    })
  },
  props: {
    search: {
      type: Array,
    },
    showPeriods: {
      type: Boolean,
      default: true,
    },
    fixedValue: {
      type: String,
    },
  },
  watch: {
    period: {
      handler(newValue) {
        if (newValue[0] && Date.parse(newValue[0])) {
          this.dateFrom = this.$d(new Date(newValue[0]), 'shortDate')
        } else {
          this.dateFrom = null
        }
        if (newValue[1] && Date.parse(newValue[1])) {
          this.dateTo = this.$d(new Date(newValue[1]), 'shortDate')
        } else {
          this.dateTo = null
        }
      },
    },
  },
  components: {
    DateField,
    DatePickerPair
  }
}
</script>
<style lang="scss" scoped>
.dialog {
  .heaader {
    background: var(--v-secondary__green_chosen-base);
    text-align: right;
    padding: 5px 5px 0px 0px;
    min-height: 50px;
    display: flex;
    justify-content: space-between;
    .title {
      margin: auto 0;
      padding-left: 40px !important;
      text-align: left;
      font-weight: 600;
      font-size: 24px;
      max-width: calc(100% - 50px);
    }
  }
}


.btn-period-type {
  font-weight: 400;
  height: 40px;
  border-width: 2px;
  border-radius: 31px;
  border-color: var(--black-color);
  letter-spacing: -0.02em;
  font-size: 16px;
  font-weight: 400;
  height: 40px;
  border-width: 2px;
  border-radius: 31px;
  border-color: var(--black-color);
  letter-spacing: -0.02em;
  font-size: 16px;
}
</style>
<style lang="scss">
#periodDialogBtn {
  min-width: 100%;
  height: 40px;
  border-radius: 31px;
  background-color: var(--white-color) !important;
  border: 2px solid var(--v-main__green-base) !important;
  .v-chip__content {
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
    width: 100%;
  }
}
</style>
